export const PAYMENT_FAILURE_STORAGE_KEY = 'digirasa_payment_failures';

const DEFAULT_COMPLETE_PAYMENT_RETRY_MINUTES = 15;

/** @returns {number} */
export function getCompletePaymentRetryWindowMs() {
  const raw = process.env.REACT_APP_COMPLETE_PAYMENT_RETRY_MINUTES;
  if (raw == null || String(raw).trim() === '') {
    return DEFAULT_COMPLETE_PAYMENT_RETRY_MINUTES * 60 * 1000;
  }
  const minutes = Number.parseInt(String(raw).trim(), 10);
  if (!Number.isFinite(minutes) || minutes < 0) {
    return DEFAULT_COMPLETE_PAYMENT_RETRY_MINUTES * 60 * 1000;
  }
  return minutes * 60 * 1000;
}

/**
 * @param {unknown} raw
 * @returns {Record<string, number>}
 */
function normalizeFailureMap(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out = /** @type {Record<string, number>} */ ({});
  for (const [key, value] of Object.entries(raw)) {
    const orderNumber = String(key || '').trim();
    const failedAt = Number(value);
    if (orderNumber && Number.isFinite(failedAt) && failedAt > 0) {
      out[orderNumber] = failedAt;
    }
  }
  return out;
}

/** @returns {Record<string, number>} */
function readFailureMap() {
  try {
    const raw = localStorage.getItem(PAYMENT_FAILURE_STORAGE_KEY);
    if (!raw) return {};
    return normalizeFailureMap(JSON.parse(raw));
  } catch {
    return {};
  }
}

/** @param {Record<string, number>} map */
function writeFailureMap(map) {
  try {
    localStorage.setItem(PAYMENT_FAILURE_STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

/**
 * @param {number} [now]
 * @returns {Record<string, number>}
 */
export function pruneExpiredPaymentFailures(now = Date.now()) {
  const windowMs = getCompletePaymentRetryWindowMs();
  const map = readFailureMap();
  let changed = false;
  for (const [orderNumber, failedAt] of Object.entries(map)) {
    if (now - failedAt > windowMs) {
      delete map[orderNumber];
      changed = true;
    }
  }
  if (changed) writeFailureMap(map);
  return map;
}

/**
 * @param {string} orderNumber
 * @param {number} [failedAt]
 */
export function recordPaymentFailure(orderNumber, failedAt = Date.now()) {
  const key = String(orderNumber || '').trim();
  if (!key || !Number.isFinite(failedAt)) return;
  const map = pruneExpiredPaymentFailures(failedAt);
  map[key] = failedAt;
  writeFailureMap(map);
}

/**
 * @param {string} orderNumber
 */
export function clearPaymentFailure(orderNumber) {
  const key = String(orderNumber || '').trim();
  if (!key) return;
  const map = readFailureMap();
  if (!(key in map)) return;
  delete map[key];
  writeFailureMap(map);
}

/**
 * @param {string} orderNumber
 * @returns {number | null}
 */
export function getPaymentFailureAt(orderNumber) {
  const key = String(orderNumber || '').trim();
  if (!key) return null;
  const map = pruneExpiredPaymentFailures();
  const failedAt = map[key];
  return Number.isFinite(failedAt) ? failedAt : null;
}

/**
 * @param {string} orderNumber
 * @param {number} [now]
 * @returns {boolean}
 */
export function isWithinCompletePaymentRetryWindow(orderNumber, now = Date.now()) {
  const failedAt = getPaymentFailureAt(orderNumber);
  if (!failedAt) return false;
  return now - failedAt <= getCompletePaymentRetryWindowMs();
}

/**
 * Milliseconds until the retry window ends for an order, or null if not in window.
 * @param {string} orderNumber
 * @param {number} [now]
 * @returns {number | null}
 */
export function getCompletePaymentRetryRemainingMs(orderNumber, now = Date.now()) {
  const failedAt = getPaymentFailureAt(orderNumber);
  if (!failedAt) return null;
  const remaining = failedAt + getCompletePaymentRetryWindowMs() - now;
  return remaining > 0 ? remaining : null;
}

/**
 * Soonest expiry among orders still inside the retry window.
 * @param {string[]} orderNumbers
 * @param {number} [now]
 * @returns {number | null}
 */
export function getNearestCompletePaymentRetryExpiryMs(orderNumbers, now = Date.now()) {
  let nearest = null;
  for (const orderNumber of orderNumbers) {
    const remaining = getCompletePaymentRetryRemainingMs(orderNumber, now);
    if (remaining == null) continue;
    if (nearest == null || remaining < nearest) nearest = remaining;
  }
  return nearest;
}
